/**
 * How a beta feature is going: who turned it on and who gave up.
 *
 * "Voltaram atrás" is the number worth watching — adoption alone says a button was found, not
 * that the feature was liked. The median days until someone reverts is next to it for the same
 * reason: giving up on day one and giving up after three weeks are different stories.
 *
 * **Why they gave up is here too**, in `OptInExitSurveyResults`, and it is a second request rather
 * than a field of `/adoption`: the screen composes two independent endpoints, which is what keeps
 * R23 intact — no BFF module learned about another one.
 *
 * **The units of this screen are people.** Every card, the chart and — since R33 made the exit
 * questionnaire one answer per person per version — the embedded questionnaire block too, which
 * used to be the one exception and used to carry a caveat saying so.
 *
 * The one place two units still meet is the line under the chart, and that is deliberate: the bars
 * count people, the line counts toggle events, and one person switching on, off and on again in a
 * day is one person and three events. There the two units are the information.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import { UTButton, UTLoading } from '@group-link-one/gl-ui/utilities';
import { useState } from 'react';
import type { JSX } from 'react';
import { AdoptionSeriesChart } from './Components/AdoptionSeriesChart';
import { AdoptionUsersTable } from './Components/AdoptionUsersTable';
import { OptInExitSurveyResults } from './Components/OptInExitSurveyResults';
import { OptInPurgeDialog } from './Components/OptInPurgeDialog';
import { OPT_IN_FEATURE_PURGE } from '../../Constants/permissions';
import { useDeleteOptInUser } from '../../Hooks/useDeleteOptInUser';
import { useExitSurveys } from '../../Hooks/useExitSurveys';
import { useOptInAdoption } from '../../Hooks/useOptInAdoption';
import { usePurgeOptInFeature } from '../../Hooks/usePurgeOptInFeature';
import { useOptInFeatureUsers } from '../../Hooks/useOptInFeatureUsers';
import type { OptInFeature, OptInFeatureUser } from '../../Types/OptInFeatures.types';
import { formatCount, formatMedianDays, formatRate, formatUnits } from '../../Utils/adoptionFormat';
import { useProfile } from '@/Auth/contexts/ProfileContext';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { useNavigate } from 'react-router-dom';
import { ConfirmationDialog } from '@/Shared/components/ConfirmationDialog/ConfirmationDialog';
import { MetricCard } from '@/Shared/components/MetricCard/MetricCard';
import { Pagination } from '@/Shared/components/Pagination/Pagination';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';

const screenStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const identityStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const nameStyle = css`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
`;

const keyStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
`;

const spacerStyle = css`
  flex: 1;
`;

const metricsStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(220px, 100%), 1fr));
  gap: var(--space-base);
`;

/*
  No two-column row any more. The chart and the questionnaire used to share one, back when the
  second was a link with two sentences in it; a block that draws a card per question needs the
  width, and stacking them also puts the units in reading order — people above, responses below.
*/
const panelStyle = css`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  padding: var(--space-ml);
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  min-width: 0;
`;

const panelTitleStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const panelHintStyle = css`
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const tableFooterStyle = css`
  display: flex;
  justify-content: flex-end;
`;

const errorStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-ml);
  border: 1px solid var(--color-danger);
  border-left: 3px solid var(--color-danger);
  border-radius: var(--radius-card);
  background: var(--color-danger-subtle);
  font-size: var(--font-size-sm);
`;

const noticeStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-ml);
  border: 1px solid var(--color-border-strong);
  border-left: 3px solid var(--color-border-strong);
  border-radius: var(--radius-card);
  background: var(--color-surface-hover);
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const noticeTitleStyle = css`
  font-weight: var(--font-weight-semibold);
`;

const errorTitleStyle = css`
  font-weight: var(--font-weight-semibold);
`;

const USERS_PAGE_SIZE = 25;

export interface OptInFeatureAdoptionProps {
  featureId: string;
  /** The feature itself, when the detail request already answered. Only names the screen. */
  feature?: OptInFeature;
  onBack: () => void;
}

export const OptInFeatureAdoption = ({
  featureId,
  feature,
  onBack,
}: OptInFeatureAdoptionProps): JSX.Element => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [isPurging, setIsPurging] = useState(false);
  /** The person the confirmation is about. `null` closes it. */
  const [toErase, setToErase] = useState<OptInFeatureUser | null>(null);
  /**
   * Shown after a purge lands, and it is not decoration.
   *
   * The dashboards cache the opt-in state in `localStorage` to kill the flicker (R19b), so somebody
   * with the feature on can still see the new menu for one more refresh, until the bootstrap
   * answers and overwrites it. Without this sentence the purge looks like it did not work.
   */
  const [purgedNotice, setPurgedNotice] = useState<string | null>(null);

  const { profile } = useProfile();
  const { pages: privileges } = usePrivileges();

  /*
    Two gates, as everywhere else in this panel: the flag says this screen offers erasing, and
    `opt_in_feature:purge` on the profile says the BFF would allow it. Either missing hides both
    actions — and it is `opt_in_feature:purge`, not `survey:purge`: holding one right does not
    imply the other.
  */
  const canErase =
    privileges.BUSINESS_UNITS.OPT_IN_FEATURES.PURGE &&
    (profile?.permissions?.includes(OPT_IN_FEATURE_PURGE) ?? false);

  const { data: adoption, isLoading, error } = useOptInAdoption(featureId);
  /** Which questionnaire is asked on the way out, and whether any of them is asked at all (R35). */
  const {
    surveys: exitSurveys,
    served: servedExitSurvey,
    isLoading: isLoadingExitSurveys,
  } = useExitSurveys(featureId);
  const purge = usePurgeOptInFeature(featureId);
  const eraseUser = useDeleteOptInUser(featureId);
  const { data: usersPage, isLoading: isLoadingUsers } = useOptInFeatureUsers(featureId, {
    page,
    limit: USERS_PAGE_SIZE,
  });

  if (isLoading) {
    return (
      <MainContent id='opt-in-adoption-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <UTLoading type='dots' />
      </MainContent>
    );
  }

  if (error || !adoption) {
    return (
      <MainContent id='opt-in-adoption-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <div id='opt-in-adoption-error' className={errorStyle} role='alert'>
          <span className={errorTitleStyle}>Não foi possível carregar a adoção</span>
          <span>{error?.message ?? 'Funcionalidade não encontrada.'}</span>
        </div>
      </MainContent>
    );
  }

  const name = adoption.featureName ?? feature?.name ?? adoption.featureKey;
  const isActive = feature?.isActive ?? true;

  return (
    <MainContent id='opt-in-adoption-main' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={screenStyle}>
        <div className={headerStyle}>
          <div className={identityStyle}>
            <span className={nameStyle}>{name}</span>
            <span className={keyStyle}>{adoption.featureKey}</span>
          </div>
          <StatusTag id='opt-in-adoption-state' status={isActive ? 'success' : 'neutral'} dot>
            {isActive ? 'Ativa' : 'Desativada — todos no comportamento antigo'}
          </StatusTag>
          <div className={spacerStyle} />
          {canErase ? (
            <UTButton
              fitContent
              variant='secondary'
              id='opt-in-adoption-purge'
              text='Apagar dados de uso'
              icon='Misc.Trash'
              state='danger'
              onClick={() => {
                setIsPurging(true);
              }}
            />
          ) : null}
          <UTButton
            fitContent
            variant='secondary'
            id='opt-in-adoption-back'
            text='Voltar'
            icon='Arrows.ArrowLeft'
            onClick={onBack}
          />
        </div>

        {purgedNotice ? (
          <div id='opt-in-adoption-purged-notice' className={noticeStyle} role='status'>
            <span className={noticeTitleStyle}>{purgedNotice}</span>
            <span>
              Quem estava com a funcionalidade ligada pode ver o comportamento novo por{' '}
              <strong>mais um refresh</strong>: o dashboard guarda o estado em cache para não piscar
              na abertura, e só troca quando o bootstrap responde. Não é o purge falhando.
            </span>
          </div>
        ) : null}

        <div className={metricsStyle}>
          <MetricCard
            id='opt-in-adoption-enabled-now'
            label='Ligados agora'
            value={formatCount(adoption.enabledNow)}
            icon='Communication.CheckCircle'
            tone='success'
          />
          <MetricCard
            id='opt-in-adoption-ever-enabled'
            label='Já ligaram'
            value={formatCount(adoption.everEnabled)}
            icon='Users.Users'
            footnote='Inclui quem depois voltou atrás.'
          />
          <MetricCard
            id='opt-in-adoption-reverted'
            label='Voltaram atrás'
            value={formatCount(adoption.reverted)}
            icon='Arrows.ArrowCounterClockwise'
            tone='danger'
          />
          <MetricCard
            id='opt-in-adoption-revert-rate'
            label='Taxa de arrependimento'
            value={formatRate(adoption.revertRate)}
            icon='Misc.ChartLineUp'
            tone='danger'
            footnote='Voltaram atrás sobre quem já ligou.'
          />
          <MetricCard
            id='opt-in-adoption-re-enabled'
            label='Religaram'
            value={formatCount(adoption.reEnabled)}
            icon='Arrows.ArrowsClockwise'
          />
          <MetricCard
            id='opt-in-adoption-median-days'
            label='Mediana até desistir'
            value={formatMedianDays(adoption.medianDaysUntilRevert)}
            icon='Timetable.Clock'
            footnote='Da ativação até o abandono.'
          />
        </div>

        <section className={panelStyle}>
          <h2 className={panelTitleStyle}>Pessoas que ligaram e desligaram, por dia</h2>
          {/*
            The bars count people; this line is where the toggles behind them are. `people` here
            is the window's own figure, not the sum of the daily ones — whoever toggled on two
            days is in both and is still one person.
          */}
          <p className={panelHintStyle}>
            {formatUnits(adoption.toggleTotals, 'evento', 'eventos')} no período
          </p>
          <AdoptionSeriesChart id='opt-in-adoption-series' series={adoption.series} />
        </section>

        {/*
          Why people leave, on the screen that measures the leaving — the panel used to be a link
          and is now the answer, with the link kept for everything it does not embed.

          R23 is untouched: this is a second, independent request (`GET /v1/surveys/:id/results`)
          composed here in the screen, not a field the adoption payload learned to carry. It counts
          people like everything else here, so it no longer needs the toggle totals: they were only
          ever passed to reconcile two units that are now one (R22b/R33).
        */}
        <OptInExitSurveyResults
          surveys={exitSurveys}
          served={servedExitSurvey}
          isLoading={isLoadingExitSurveys}
          canReadResults={privileges.BUSINESS_UNITS.SURVEYS.RESULTS}
          onOpenResults={id => {
            navigate(`/surveys/${id}/results`);
          }}
        />

        <section className={panelStyle}>
          <h2 className={panelTitleStyle}>Pessoas</h2>
          <p className={panelHintStyle}>
            Identificadas pelo <code>platformUserId</code>. O BFF não guarda nome nem e-mail de
            usuário da plataforma, então não há o que mostrar além do id.
          </p>

          {isLoadingUsers && !usersPage ? (
            <UTLoading type='dots' />
          ) : (
            <>
              <AdoptionUsersTable
                id='opt-in-adoption-users'
                users={usersPage?.users ?? []}
                onErase={canErase ? setToErase : undefined}
                isErasing={eraseUser.isPending}
              />
              <div className={tableFooterStyle}>
                <Pagination
                  id='opt-in-adoption-users-pagination'
                  page={page}
                  pageSize={USERS_PAGE_SIZE}
                  total={usersPage?.total ?? 0}
                  onChange={setPage}
                />
              </div>
            </>
          )}
        </section>
      </div>

      {/* UTModal keeps its content in the a11y tree when closed, so mount it only when needed. */}
      {isPurging ? (
        <OptInPurgeDialog
          featureKey={adoption.featureKey}
          featureName={name}
          enabledNow={adoption.enabledNow}
          everEnabled={adoption.everEnabled}
          isWorking={purge.isPending}
          onClose={() => {
            setIsPurging(false);
          }}
          onPurge={confirmKey => {
            /*
              Nothing on screen changes before the `200`. A `400` — the key did not match — deleted
              nothing, and the numbers have to keep saying so.
            */
            purge.mutate(confirmKey, {
              onSuccess: result => {
                setPurgedNotice(
                  result.deletedOptIns === 1
                    ? 'Dados de 1 pessoa apagados.'
                    : `Dados de ${result.deletedOptIns.toLocaleString('pt-BR')} pessoas apagados.`,
                );
              },
              onSettled: () => {
                setIsPurging(false);
              },
            });
          }}
        />
      ) : null}

      {/*
        No key field here, and that is deliberate: asking for it on every row turns the guard into a
        ritual, and a ritual repeated is a thing that stops being read. The person is named instead.
      */}
      {toErase ? (
        <ConfirmationDialog
          open
          title='Apagar os dados de opt-in desta pessoa'
          message={`A pessoa ${toErase.platformUserId} volta ao comportamento padrão — do novo para o antigo — e o histórico dela some: primeira ativação, quantas vezes ligou e desligou, motivo de saída. Ela passa a contar como alguém que nunca optou, e deixa de ser elegível para as pesquisas condicionadas a esta funcionalidade até optar de novo, quando o relógio recomeça do zero. A resposta de pesquisa dela NÃO é apagada, então a enquete não reaparece para ela — isso é o botão da tela de respondentes. Não há como desfazer.`}
          confirmText='Apagar dados desta pessoa'
          cancelText='Cancelar'
          variant='danger'
          isLoading={eraseUser.isPending}
          onClose={() => {
            setToErase(null);
          }}
          onConfirm={() => {
            eraseUser.mutate(toErase.platformUserId, {
              onSettled: () => {
                setToErase(null);
              },
            });
          }}
        />
      ) : null}
    </MainContent>
  );
};
